import { HttpService } from '@/data/http/http-service';
import { CalculateFestival } from '@/domain/impact-calculate-festival/entities/calculate-festival.entity';
import { CalculateFestivalCo2 } from '@/domain/impact-calculate-festival/entities/calculate-festival-co2.entity';
import { CalculateFestivalEnv } from '@/domain/impact-calculate-festival/entities/calculate-festival-env.entity';
import { CalculateFestivalEss } from '@/domain/impact-calculate-festival/entities/calculate-festival-ess.entity';
import { ImpactCalculateFestivalGateway } from '@/domain/impact-calculate-festival/gateways/impact-calculate-festival.gateway';
import {
  ModelVersionENV,
  ModelVersionESS,
} from '@/domain/project/entities/project.entity';

import { getAllImpactFestivalForAllProjectsDtoSchema } from './all-impact-for-all-projects/dto.schema';
import { AllImpactFestivalForAllProjectsMapper } from './all-impact-for-all-projects/mapper';
import { calculateFestivalCo2DtoSchema } from './co2/dto.type';
import { CalculateFestivalCo2Mapper } from './co2/mapper';
import { calculateFestivalEnvDtoSchema } from './env/dto.type';
import { CalculateFestivalEnvMapper } from './env/mapper';
import { calculateFestivalEssDtoSchema } from './ess/dto.type';
import { CalculateFestivalEssMapper } from './ess/mapper';

export class ApiImpactCalculateFestivalHttpGateway implements ImpactCalculateFestivalGateway {
  private httpProject = new HttpService({
    baseURL: import.meta.env.VITE_API_PROJECT,
  });
  private httpImpact = new HttpService({
    baseURL: import.meta.env.VITE_API_IMPACT,
  });

  async runImpactCalculateFestivalCo2ForProject(
    projectId: number,
  ): Promise<CalculateFestivalCo2> {
    const url = '/calculate/co2';
    const data = { projectId: projectId };
    const response = await this.httpImpact.post(url, data);
    const result = calculateFestivalCo2DtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const calculateCo2 = CalculateFestivalCo2Mapper.dtoToEntity(result.data);
    return calculateCo2;
  }

  async runImpactCalculateFestivalEnvForProject(
    projectId: number,
    modelVersion: ModelVersionENV,
  ): Promise<CalculateFestivalEnv> {
    const url = '/calculate/env';
    const data = { projectId: projectId };
    const options = { additionalHeaders: { 'x-model-version': modelVersion } };
    const response = await this.httpImpact.post(url, data, options);
    const result = calculateFestivalEnvDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const calculateEnv = CalculateFestivalEnvMapper.dtoToEntity(result.data);
    return calculateEnv;
  }

  async runImpactCalculateFestivalEssForProject(
    projectId: number,
    modelVersion: ModelVersionESS,
  ): Promise<CalculateFestivalEss> {
    const url = '/calculate/ess';
    const data = { projectId: projectId };
    const options = { additionalHeaders: { 'x-model-version': modelVersion } };
    const response = await this.httpImpact.post(url, data, options);
    const result = calculateFestivalEssDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const calculateEss = CalculateFestivalEssMapper.dtoToEntity(result.data);
    return calculateEss;
  }

  async getAllImpactCalculateFestivalForAllProjects(): Promise<
    { projectId: number; calculate: CalculateFestival }[]
  > {
    const url = '/festivals';
    const options = { params: { $limit: 1000 } };
    const response = await this.httpProject.get(url, options);
    const result = getAllImpactFestivalForAllProjectsDtoSchema.safeParse(
      response.data,
    );
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const calculate = AllImpactFestivalForAllProjectsMapper.dtoToEntity(
      result.data,
    );
    return calculate;
  }
}
